/**
 * Real `ReportingAPI` implementation backed by the HTTP client
 * (ADR-0115). Slices A + B + C are wired here.
 *
 *   getOverview:    GET /v1/sites/:site_id/reporting/overview?range=…
 *                   → ReportingOverview (4 KPI tiles + window + per-tile deltas)
 *
 *   getTraffic:     GET /v1/sites/:site_id/reporting/traffic?range=…
 *                   → ReportingTrafficSeries (zero-filled human-vs-agent
 *                   buckets across the window; granularity server-resolved)
 *
 *   getBreakdowns:  GET /v1/sites/:site_id/reporting/breakdowns?range=…
 *                   → ReportingBreakdowns (lifecycle counts, top 5 agents,
 *                   top 8 skills — one round trip, three widgets)
 *
 * Read-only. The server resolves the closed `range` enum to a UTC
 * window and aggregates over `audit_record` under RLS; this bag just
 * forwards the typed result. A 404 (cross-tenant / unknown site)
 * surfaces as the http client's mapped ApiError — the view treats it
 * as an empty/unavailable state.
 */

import type {
  ReportingAgentActivity,
  ReportingBreakdowns,
  ReportingOverview,
  ReportingTrafficSeries,
  SiteId,
} from '@sill/types';
import type {
  ReportingAPI,
  ReportingAgentActivityQuery,
  ReportingBreakdownsQuery,
  ReportingOverviewQuery,
  ReportingTrafficQuery,
} from '@sill/types/api';

import type { HttpClient } from './http-client.js';

export interface RealReportingDeps {
  readonly http: HttpClient;
}

export function createRealReportingApi(deps: RealReportingDeps): ReportingAPI {
  return {
    async getOverview(
      site_id: SiteId,
      query: ReportingOverviewQuery,
    ): Promise<ReportingOverview> {
      const params = new URLSearchParams({ range: query.range });
      const res = await deps.http.request<ReportingOverview>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/reporting/overview?${params.toString()}`,
      });
      // The endpoint always returns a body on 200; an empty response
      // means a transport/route problem — surface it rather than
      // return a fake overview.
      if (res === undefined) {
        throw new Error('reporting_overview_empty_response');
      }
      return res;
    },

    async getTraffic(
      site_id: SiteId,
      query: ReportingTrafficQuery,
    ): Promise<ReportingTrafficSeries> {
      const params = new URLSearchParams({ range: query.range });
      const res = await deps.http.request<ReportingTrafficSeries>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/reporting/traffic?${params.toString()}`,
      });
      if (res === undefined) {
        throw new Error('reporting_traffic_empty_response');
      }
      return res;
    },

    async getBreakdowns(
      site_id: SiteId,
      query: ReportingBreakdownsQuery,
    ): Promise<ReportingBreakdowns> {
      const params = new URLSearchParams({ range: query.range });
      const res = await deps.http.request<ReportingBreakdowns>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/reporting/breakdowns?${params.toString()}`,
      });
      if (res === undefined) {
        throw new Error('reporting_breakdowns_empty_response');
      }
      return res;
    },

    async getAgentActivity(
      site_id: SiteId,
      query: ReportingAgentActivityQuery,
    ): Promise<ReportingAgentActivity> {
      // ADR-0161 — comma-joined `agent_id` list + closed `range` enum.
      // The server re-validates both (≤5 entries, each matching
      // `^agent_[a-z0-9_]{1,64}$`); the client-side URLSearchParams
      // build just wires the shape without a second regex pass.
      const params = new URLSearchParams({ range: query.range });
      params.set('agent_id', query.agent_ids.join(','));
      const res = await deps.http.request<ReportingAgentActivity>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/reporting/agent-activity?${params.toString()}`,
      });
      if (res === undefined) {
        throw new Error('reporting_agent_activity_empty_response');
      }
      return res;
    },
  };
}
